import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowRight, Reply, Volume2, Square } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharedLetter() {
  const { shareId } = useParams();
  const [letter, setLetter] = useState(null);
  const [missing, setMissing] = useState(false);
  const [speaking, setSpeaking] = useState(null);
  const audioRef = useRef(null);
  const stopRef = useRef(false);

  const stopSpeech = () => {
    stopRef.current = true;
    audioRef.current?.pause();
    audioRef.current = null;
    setSpeaking(null);
  };

  useEffect(() => () => stopSpeech(), []);

  const playBlob = (blob) =>
    new Promise((resolve) => {
      const audio = new Audio(URL.createObjectURL(blob));
      audioRef.current = audio;
      audio.onended = () => resolve(true);
      audio.onerror = () => resolve(false);
      audio.play().catch(() => resolve(false));
    });

  const listen = async (which, index) => {
    const key = `${which}-${index}`;
    if (speaking === key) {
      stopSpeech();
      return;
    }
    stopSpeech();
    stopRef.current = false;
    setSpeaking(key);
    let part = 0;
    let total = 1;
    try {
      while (part < total) {
        if (stopRef.current) return;
        const res = await axios.get(`${API}/shared/letters/${shareId}/speech`, {
          params: { which, index, part },
          responseType: "blob",
        });
        total = Number(res.headers["x-parts"] || 1);
        if (stopRef.current) return;
        const finished = await playBlob(res.data);
        if (!finished || stopRef.current) return;
        part += 1;
      }
    } catch {
      /* the reader can try again */
    } finally {
      if (!stopRef.current) setSpeaking(null);
    }
  };

  useEffect(() => {
    axios
      .get(`${API}/shared/letters/${shareId}`)
      .then(({ data }) => setLetter(data))
      .catch(() => setMissing(true));
  }, [shareId]);

  if (missing) {
    return (
      <div className="mx-auto max-w-xl px-5 py-24 text-center" data-testid="shared-letter-missing">
        <p className="text-sm text-ink-soft">This letter link is no longer available.</p>
        <Link to="/" className="mt-6 inline-flex text-sm text-brand hover:underline">
          Go to ChatMiLu
        </Link>
      </div>
    );
  }

  if (!letter) {
    return (
      <div className="mx-auto max-w-xl px-5 py-24" data-testid="shared-letter-loading">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-5 py-16 sm:px-8" data-testid="shared-letter-page">
      <Link to="/">
        <img src="/logo.png" alt="ChatMiLu" className="h-10 w-auto" />
      </Link>

      <p className="mt-10 text-xs uppercase tracking-[0.2em] text-ink-soft" data-testid="shared-letter-by">
        {letter.shared_by} shared a letter with you
      </p>
      <h1
        dir="auto"
        data-testid="shared-letter-title"
        className="mt-3 font-display text-3xl font-extrabold tracking-tighter sm:text-4xl"
      >
        {letter.title}
      </h1>
      {letter.labels?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {letter.labels.map((l) => (
            <span
              key={l}
              className="rounded-full border border-brand/30 bg-brand/10 px-2.5 py-1 text-[11px] text-brand"
            >
              {l}
            </span>
          ))}
        </div>
      )}

      <div className="mt-6 rounded-3xl border border-white/10 bg-panel p-7">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-[11px] uppercase tracking-widest text-brand">The letter</p>
          <button
            data-testid="shared-letter-listen"
            onClick={() => listen("letter", 0)}
            className="ms-auto inline-flex min-h-[36px] items-center gap-2 rounded-full border border-white/15 px-4 text-xs font-semibold text-ink-soft transition-colors hover:border-brand/50 hover:text-brand active:scale-95"
          >
            {speaking === "letter-0" ? <Square className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
            {speaking === "letter-0" ? "Stop" : "Hear it"}
          </button>
        </div>
        <p
          dir="auto"
          data-testid="shared-letter-text"
          className="mt-3 whitespace-pre-wrap text-sm leading-relaxed"
        >
          {letter.translation}
        </p>
      </div>

      {letter.replies.map((r, i) => (
        <div
          key={r.reply_id || r.created_at || `reply-${i}`}
          data-testid={`shared-letter-reply-${i}`}
          className="mt-4 rounded-3xl border border-brand/25 bg-panel p-7"
        >
          <div className="flex flex-wrap items-center gap-3">
            <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-brand">
              <Reply className="h-3.5 w-3.5" /> Reply {letter.replies.length > 1 ? i + 1 : ""}
            </p>
            <button
              data-testid={`shared-letter-reply-listen-${i}`}
              onClick={() => listen("reply", i)}
              className="ms-auto inline-flex min-h-[36px] items-center gap-2 rounded-full border border-white/15 px-4 text-xs font-semibold text-ink-soft transition-colors hover:border-brand/50 hover:text-brand active:scale-95"
            >
              {speaking === `reply-${i}` ? <Square className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
              {speaking === `reply-${i}` ? "Stop" : "Hear it"}
            </button>
          </div>
          <p dir="auto" className="mt-3 whitespace-pre-wrap text-sm leading-relaxed">
            {r.translation}
          </p>
          {r.draft_en && (
            <p dir="auto" className="mt-3 whitespace-pre-wrap border-t border-white/10 pt-3 text-sm text-ink-soft">
              {r.draft_en}
            </p>
          )}
        </div>
      ))}

      <Link
        to="/signup"
        data-testid="shared-letter-cta"
        className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-brand hover:underline"
      >
        Read and answer any letter with ChatMiLu <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
